class BanzrcDtFinale extends HTMLElement {
  connectedCallback() {
    this.configurator = [...document.querySelectorAll('banzrc-gt-configurator')]
      .find((element) => element.dataset.productId === this.dataset.productId);
    this.purchaseSection = this.configurator?.closest('.shopify-section');
    this.rows = [...this.querySelectorAll('[data-finale-variant]')].map((row) => ({ ...row.dataset }));
    this.sceneButtons = [...this.querySelectorAll('[data-finale-scene]')];
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.sceneButtons.forEach((button) => button.addEventListener('click', () => {
      const normalize = value => String(value || '').trim().toLowerCase();
      const choice = this.mobile.matches && [...(this.configurator?.querySelectorAll('[data-color]') || [])].find(item => normalize(item.dataset.color) === normalize(button.dataset.finaleScene));
      if (choice && !choice.disabled) choice.click(); else this.showScene(button);
    }));
    this.querySelector('[data-finale-change]')?.addEventListener('click', () => this.goToConfiguration());
    this.querySelector('[data-finale-chat]')?.addEventListener('click', () => this.openChat());
    this.querySelector('[data-finale-cart]')?.addEventListener('click', () => this.purchase());
    this.querySelector('[data-sticky-cart]')?.addEventListener('click', () => this.purchase());
    this.onBreakpointChange = () => this.showScene(this.activeScene || this.sceneButtons[0], true);
    this.mobile.addEventListener('change', this.onBreakpointChange);

    if (this.configurator) {
      this.observer = new MutationObserver(() => this.render());
      this.observer.observe(this.configurator, { attributes: true, subtree: true, attributeFilter: ['aria-pressed', 'data-selection-complete'] });
    }
    this.purchaseObserver = new IntersectionObserver(([entry]) => {
      this.purchasePassed = entry.boundingClientRect.bottom < 0;
      this.updateSticky();
    });
    if (this.purchaseSection) this.purchaseObserver.observe(this.purchaseSection);
    this.finaleObserver = new IntersectionObserver(([entry]) => {
      this.finaleVisible = entry.isIntersecting;
      this.dataset.mobileInView = String(entry.isIntersecting);
      this.updateSticky();
    }, { threshold: 0.05 });
    const stage = this.querySelector('.banzrc-gt-finale__stage');
    if (stage) this.finaleObserver.observe(stage);
    this.showScene(this.sceneButtons[0], true);
    this.render();
  }

  get state() {
    const color = this.configurator?.selectedColor || null;
    const power = this.configurator?.selectedPower || null;
    const normalize = (value) => String(value || '').trim().toLowerCase();
    const selectedVariant = this.configurator?.selectedVariant;
    const row = selectedVariant
      ? this.rows.find((item) => String(item.id) === String(selectedVariant.id))
      : this.rows.find((item) => normalize(item.color) === normalize(color) && normalize(item.power) === normalize(power));
    return { color, power, row, ready: Boolean(row && row.available === 'true') };
  }

  render() {
    const { color, power, row, ready } = this.state;
    const config = ready
      ? [color, power === '__single__' ? '' : power].filter(Boolean).join(' · ')
      : [color, power && power !== '__single__' ? power : ''].filter(Boolean).join(' · ') || this.dataset.chooseColorPower;
    const price = ready ? row.price : this.dataset.chooseConfiguration;
    this.set('[data-finale-config]', config);
    this.set('[data-finale-price]', price);
    this.set('[data-sticky-config]', config);
    this.set('[data-sticky-price]', ready ? price : '—');
    this.set('[data-finale-cart]', ready ? this.dataset.addToCart : this.dataset.chooseConfiguration);
    this.set('[data-sticky-cart]', ready ? this.dataset.addToCart : this.dataset.chooseConfiguration);
    if (color) {
      const scene = this.sceneButtons.find((button) => button.dataset.finaleScene.trim().toLowerCase() === color.trim().toLowerCase());
      if (scene) this.showScene(scene);
    }
  }

  set(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }

  showScene(button, force = false) {
    if (!button) return;
    this.activeScene = button;
    this.sceneButtons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
    this.dataset.finaleTone = button.dataset.finaleTone || 'black';
    const image = this.querySelector('[data-finale-image]');
    const sticky = this.querySelector('[data-sticky-image]');
    const source = this.mobile.matches ? button.dataset.finaleMobile : button.dataset.finaleDesktop;
    const generation = this.sceneGeneration = (this.sceneGeneration || 0) + 1;
    if (!source || !image || (!force && image.src === source)) return;
    image.classList.add('is-changing');
    const next = new Image();
    next.onload = () => {
      if (generation !== this.sceneGeneration || !this.isConnected) return;
      image.src = source;
      image.alt = button.dataset.finaleAlt;
      image.classList.remove('is-changing');
      if (sticky) sticky.src = source;
    };
    next.onerror = () => { if (generation === this.sceneGeneration) image.classList.remove('is-changing'); };
    next.src = source;
  }

  goToConfiguration() {
    document.getElementById('banzrc-dt-configuration')?.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
  }

  purchase() {
    if (!this.state.ready) return this.goToConfiguration();
    const button = this.purchaseSection?.querySelector('add-to-cart-component button');
    if (button && !button.disabled) button.click();
  }

  openChat() {
    const chat = document.querySelector('shopify-chat');
    if (!chat) return;
    if (!window.matchMedia('(max-width: 749px)').matches) {
      chat.shadowRoot?.querySelector('[part="activator"]')?.click();
      return;
    }
    const show = () => {
      if (this.isConnected && chat.isConnected && typeof chat.show === 'function') chat.show();
    };
    if (typeof chat.show === 'function') show();
    else customElements.whenDefined('shopify-chat').then(show);
  }

  updateSticky() {
    const sticky = this.querySelector('[data-finale-sticky]');
    if (!sticky) return;
    const visible = Boolean(this.purchasePassed && !this.finaleVisible);
    sticky.hidden = !visible;
    requestAnimationFrame(() => sticky.classList.toggle('is-visible', visible));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.purchaseObserver?.disconnect();
    this.finaleObserver?.disconnect();
    this.mobile?.removeEventListener('change', this.onBreakpointChange);
  }
}

if (!customElements.get('banzrc-dt-finale')) customElements.define('banzrc-dt-finale', BanzrcDtFinale);
